import React, { useState, useEffect } from 'react';

import Grid from "@mui/material/Grid"
import { isMobile } from 'react-device-detect';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import "react-custom-flag-select/lib/react-custom-flag-select.min.css";

import { WEB_URL } from '../../api/URLs';
import useForm from '../../hooks/UseForm';
import { GTM_EVENTS } from '../../constants';
import { validateSignUp } from '../../validations';
import { displayError } from '../../helpers';
import Tracking from '../../services/tracking.service';
import { useNonInitialEffect } from '../../hooks/UseNonInitialEffect';
import { donationActions, fundRaiserPageAction, userActions } from '../../store/actions';
import { PasswordInput, Textfield, FlagsDropdown, MixPanelTrackedLink, ShowSpinnerOrLabel } from "../../components";
import { useLocation, useNavigate } from 'react-router-dom';

export default function() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const redirectAfterSignUpSuccessUrl = location.state?.from || WEB_URL.PROFILE();
    const userCurrentLocation = useSelector(state => state.fundraiser_page.location_details); 
    const errors = useSelector(state => state.validationErrors); 
    const [signUpInProgress, setSignUpInProgress] = useState(false);
    const [emailAlreadyExist, setEmailAlreadyExist] = useState(false);
    const userLocationCurrencyExistsInDB = useSelector(state => state.check_if_country_partner_isactive_for_donor.if_partner_active_for_donor); 

    const [state, setState] = useState({
        firstName: "",
        lastName: "",
        contact: "",
        email: "",
        password: "",
        confirmPassword: "",
        selectedCountry: {
            id: 1,
            name: "Pakistan",
            code: "PK",
        }
    });

    const updateState = (event) => {
        const { name, value } = event.target;

        setState({
            ...state,
            [name]: value
        })
    };

    const updateSelectedCurrency = (currency) => {
        setState({
            ...state,
            selectedCountry: {
                id: currency.db_id,
                name: currency.name,
            },
        })
    };

    const registerUser = async () => {
        if (!emailAlreadyExist) {
            setSignUpInProgress(true);
            const payload = {
                first_name: state.firstName,
                last_name: state.lastName,
                email: state.email,
                contact_number: state.contact,
                password: state.password,
                password_confirmation: state.confirmPassword,
                country_id: state.selectedCountry.id,
            };

            const success = await dispatch(userActions.register(payload));
            setSignUpInProgress(false);

            if (success) {
                Tracking.trackGTM({event: GTM_EVENTS.SIGNUP_COMPLETED});
                
                navigate(redirectAfterSignUpSuccessUrl, { state: { ...location.state } });
            }
        }
    }

    const { handleChange, handleSubmit } = useForm(registerUser, validateSignUp, updateState, state);

    useEffect(() => {
        Tracking.trackGTM({event: GTM_EVENTS.SIGNUP_INITIATED});
        dispatch(fundRaiserPageAction.getUserDefaultCurrency());
    }, []);

    useEffect(() => {
        if (userCurrentLocation) {
            dispatch(donationActions.checkIfCountryPartnerIsactiveDonor(userCurrentLocation.country_id));
        }
    }, [userCurrentLocation]);

    useEffect(() => {
        if (userLocationCurrencyExistsInDB) {
            setState({
                ...state,
                selectedCountry: {
                    id: userCurrentLocation.country_id,
                    name: userCurrentLocation.country_name,
                }
            });
        }
    }, [userLocationCurrencyExistsInDB])

    useNonInitialEffect(() => {
        const checkEmail = setTimeout(async () => {
            const emailExist = await dispatch(userActions.checkEmailExist(state.email))
            setEmailAlreadyExist(emailExist) 
        }, 1000)

        return () => clearTimeout(checkEmail)
    }, [state.email])



    return <React.Fragment>
        <Typography gutterBottom align='center' component="h1" variant="h5">
            Let's set up your account
        </Typography>
        <Typography gutterBottom sx={formStyles.topHeading}>Creating an account lets you to set up fundraising pages, track your donation impact, and save your preferences.</Typography>
        <Grid container spacing={2} sx={{ pt: '25px' }}>
            <Grid item xs={6}>
                <Textfield
                    id="firstName"
                    name="firstName"
                    label="First name *"
                    value={state.firstName}
                    onChange={handleChange}
                    error={displayError(errors.firstName)} 
                    helperText={errors.firstName}         
                    tabIndex="1"
                    autoFocus
                    fullWidth
                />
            </Grid>
            <Grid item xs={6}>
                <Textfield
                    id="lastName"
                    name="lastName"
                    label="Last name *"
                    value={state.lastName}
                    onChange={handleChange}
                    error={displayError(errors.lastName)}
                    helperText={errors.lastName}
                    tabIndex="2"
                    fullWidth
                />
            </Grid>
            <Grid item xs={12}>
                <Textfield
                    id="email"
                    name="email"
                    type="email"
                    label="Email address *"
                    value={state.email}
                    onChange={handleChange}
                    error={displayError(errors.email) || emailAlreadyExist}
                    helperText={emailAlreadyExist ? 'Email already taken' : errors.email}
                    tabIndex="3"
                    fullWidth />
            </Grid>
            <Grid item xs={12}>
                <Textfield
                    id="contact"
                    type="text"
                    name="contact"
                    label="Contact Number *"
                    value={state.contact}
                    onChange={handleChange}
                    error={displayError(errors.contact)}
                    helperText={errors.contact}
                    tabIndex="4"
                    fullWidth />
            </Grid>
            <Grid item xs={12}>
                <PasswordInput
                    id="password"
                    name="password"
                    label="Password *"
                    value={state.password}
                    onChange={handleChange}
                    error={displayError(errors.password)}
                    helperText={errors.password}
                    tabIndex="5"
                    fullWidth
                />
            </Grid>
            <Grid item xs={12}>
                <PasswordInput
                    id="confirmPassword"
                    name="confirmPassword"
                    label="Enter password confirmation *"
                    value={state.confirmPassword}
                    onChange={handleChange}
                    error={displayError(errors.confirmPassword)}
                    helperText={errors.confirmPassword}
                    tabIndex="6"
                    fullWidth
                />
            </Grid>
            <Grid item xs={12}>
                <Grid container alignItems={'center'} justifyContent="space-between">
                    <Grid item xs={5}>
                        Preferred Currency
                    </Grid>
                    <Grid item xs={6} sm={7} sx={formStyles.currencyDropdown}>
                        <FlagsDropdown
                            selectedOption={state.selectedCountry.name}
                            onChange={updateSelectedCurrency}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <MixPanelTrackedLink
                    type="button"
                    label="SignUp"
                    context="Create new account"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={signUpInProgress}
                    fullWidth
                >
                    <ShowSpinnerOrLabel loading={signUpInProgress} label="Signup" variant='white' />
                </MixPanelTrackedLink>
            </Grid>
        </Grid>
    </React.Fragment>
}
const formStyles = {
    topHeading: {
        width: '100%',
        paddingBottom: '10px',
        borderBottom: '1px solid #dee2e6',
    },

    currencyDropdown: {
        border: '1px solid #bdbdbd',
        padding: isMobile ? '7.5px 5px!important' : '14.5px 12px !important',
        borderRadius: '4px',
    }
}